import { createDebugger } from '../utils/debug.js';

const debug = createDebugger('swatch-prefetch', false);

/**
 * Warms the first gallery image of a colour before the shopper commits to it.
 *
 * Switching a variant refetches the product section, and only then do that variant's
 * images start downloading, so the gallery can sit empty for seconds. Hover and touch
 * both precede the click by enough to cover most of that.
 *
 * Listeners are delegated from the document because the swatches themselves are
 * replaced whenever the section reloads.
 */

// must match the widths list passed to the `pictures` snippet, or the prefetch lands on
// a URL the gallery never asks for and the bytes are wasted twice over
const SRCSET_WIDTHS = [300, 600, 900, 1200, 1500, 1800, 2100, 2400, 2700, 3000];

const SELECTOR = '[data-prefetch-image]';
const GALLERY_IMAGE = '[data-product-media-wrapper] img, .product__media-item img';

const prefetched = new Set();

/** Snap to the smallest srcset candidate that still covers the slot at this DPR. */
function widthForSlot() {
    const el = document.querySelector(GALLERY_IMAGE);
    const slot = el ? el.getBoundingClientRect().width : window.innerWidth * 0.9;
    const needed = Math.round((slot || window.innerWidth * 0.9) * (window.devicePixelRatio || 1));

    return SRCSET_WIDTHS.find((w) => w >= needed) || SRCSET_WIDTHS[SRCSET_WIDTHS.length - 1];
}

function prefetch(swatch) {
    const base = swatch.dataset.prefetchImage;
    if (!base) return;

    const url = base + (base.includes('?') ? '&' : '?') + 'width=' + widthForSlot();
    if (prefetched.has(url)) return;
    prefetched.add(url);

    // a bare Image() is enough to populate the HTTP cache, and keeping it at low
    // priority means it never competes with what is already on screen
    const img = new Image();
    if ('fetchPriority' in img) img.fetchPriority = 'low';
    img.decoding = 'async';
    img.src = url;

    debug.log('prefetching', { url });
}

function onIntent(event) {
    const swatch = event.target.closest?.(SELECTOR);
    if (swatch) prefetch(swatch);
}

// pointerover covers mouse and pen; touchstart fires early enough on touch to be worth it
document.addEventListener('pointerover', onIntent, { passive: true });
document.addEventListener('touchstart', onIntent, { passive: true });
document.addEventListener('focusin', onIntent, { passive: true });
